import { useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import Halftone from '@/components/agents/Halftone';
import { SectionHeading, tone } from '@/components/agents/primitives';

import {
  TESTIMONIALS,
  type TestimonialSource,
  type TextTestimonial,
  type VideoTestimonial,
} from '@/constants/testimonials';

import { useB2bTranslation } from './kit';

const SOURCE_NAMES: Record<TestimonialSource, string> = {
  capterra: 'Capterra',
  g2: 'G2',
  google: 'Google',
  trustpilot: 'Trustpilot',
  youtube: 'YouTube',
};

// Verbatim excerpts of real reviews (ellipses mark the cuts). Quotes stay in
// the reviewer's own words, so they are not translated.
const FEATURED = {
  id: 'jeff-wilson',
  excerpt:
    'We have tried multiple platforms that would work for us - a small business - and it was a bit overwhelming two years ago. Easy-Peasy has been a godsend. … Ironically, their human support is one of the most appreciated elements.',
};

const PICKS: { id: string; excerpt: string }[] = [
  {
    id: 'ken-poon',
    excerpt:
      'Very useful and true to “Easy Peasy” on using AI. One platform on all the tools required for business needs.',
  },
  {
    id: 'gabriel-s',
    excerpt:
      'This AI is revolutionary because it combines most of the useful AIs into a single tool. … It’s simple, it has everything.',
  },
  {
    id: 'muhammad-md-rahim',
    excerpt:
      'Very awesome and practical AI tool that can help improve our productivity and the quality of our work!',
  },
];

export default function TeamsReviews() {
  const { t } = useB2bTranslation();

  const featured = TESTIMONIALS.find(
    (item): item is VideoTestimonial =>
      item.id === FEATURED.id && item.type === 'video',
  );
  const reviews = PICKS.flatMap(({ id, excerpt }) => {
    const review = TESTIMONIALS.find(
      (item): item is TextTestimonial => item.id === id && item.type === 'text',
    );
    return review ? [{ ...review, excerpt }] : [];
  });

  return (
    <section id='reviews' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('Loved by busy teams.')}
          tail={t('In their own words.')}
        />

        <div className='mt-14 grid gap-5 lg:grid-cols-5'>
          {featured ? (
            <FeaturedVideo review={featured} excerpt={FEATURED.excerpt} />
          ) : null}
          <div className='grid gap-5 lg:col-span-2'>
            {reviews.map((review) => (
              <figure
                key={review.id}
                className={clsxm(
                  'flex flex-col justify-between gap-6 rounded-[28px] p-7',
                  tone.surface,
                  tone.line,
                )}
              >
                <blockquote lang='en' dir='ltr' className='text-start'>
                  <p className={clsxm('text-[16px] leading-7', tone.ink)}>
                    “{review.excerpt}”
                  </p>
                </blockquote>
                <figcaption className='flex items-center gap-3'>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={review.avatarUrl}
                    alt=''
                    width={36}
                    height={36}
                    loading='lazy'
                    className='h-9 w-9 flex-none rounded-full bg-[#eeeef4] object-cover'
                  />
                  <span className='min-w-0 flex-1'>
                    <span
                      className={clsxm(
                        'block truncate text-[14px] font-semibold',
                        tone.ink,
                      )}
                    >
                      {review.author}
                    </span>
                    <span className='flex items-center gap-2 text-[13px]'>
                      <span
                        className='text-[#f5a524]'
                        aria-label={t('5 out of 5 stars')}
                      >
                        ★★★★★
                      </span>
                      {review.source && review.sourceUrl ? (
                        <a
                          href={review.sourceUrl}
                          target='_blank'
                          rel='noopener noreferrer'
                          className={clsxm(
                            'truncate hover:underline',
                            tone.quiet,
                          )}
                        >
                          {SOURCE_NAMES[review.source]}
                        </a>
                      ) : null}
                    </span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedVideo({
  review,
  excerpt,
}: {
  review: VideoTestimonial;
  excerpt: string;
}) {
  const { t } = useB2bTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const play = () => {
    setPlaying(true);
    // The element mounts with its source on this render; start it next tick.
    requestAnimationFrame(() =>
      videoRef.current?.play().catch(() => undefined),
    );
  };

  return (
    <figure className='relative isolate flex flex-col overflow-hidden rounded-[28px] bg-[#0d0c1f] ring-1 ring-inset ring-white/10 lg:col-span-3'>
      <Halftone
        at='100% 100%'
        reach={75}
        className='-z-10 opacity-40 [--ht:rgb(var(--tw-color-primary-700))]'
      />
      <div className='relative aspect-video w-full overflow-hidden bg-black'>
        {playing ? (
          <video
            ref={videoRef}
            src={review.videoUrl}
            poster={review.poster}
            controls
            playsInline
            className='h-full w-full object-cover'
          />
        ) : (
          <button
            type='button'
            onClick={play}
            className='group absolute inset-0 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white'
            aria-label={t('Play {{name}}’s video review', {
              name: review.author,
            })}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={review.poster}
              alt=''
              loading='lazy'
              className='h-full w-full object-cover opacity-90 transition-opacity duration-200 group-hover:opacity-100'
            />
            <span className='absolute inset-0 bg-gradient-to-t from-[#0d0c1f]/70 via-transparent to-transparent' />
            <span className='absolute start-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-[#10101c] shadow-[0_20px_40px_-12px_rgba(0,0,0,0.6)] rtl:translate-x-1/2'>
              <svg
                viewBox='0 0 20 20'
                fill='currentColor'
                className='ms-1 h-6 w-6'
                aria-hidden='true'
              >
                <path d='M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z' />
              </svg>
            </span>
          </button>
        )}
      </div>
      <div className='flex flex-1 flex-col justify-between gap-8 p-7 sm:p-9'>
        <blockquote lang='en' dir='ltr' className='text-start'>
          <p className='text-xl font-medium leading-[1.5] tracking-[-0.01em] text-white sm:text-2xl sm:leading-[1.45]'>
            “{excerpt}”
          </p>
        </blockquote>
        <figcaption className='flex items-center justify-between gap-4'>
          <span>
            <span className='block text-[15px] font-semibold text-white'>
              {review.author}
            </span>
            <span className='text-sm text-[#9d9dbd]'>{t('Video review')}</span>
          </span>
          <span className='text-[#f5a524]' aria-label={t('5 out of 5 stars')}>
            ★★★★★
          </span>
        </figcaption>
      </div>
    </figure>
  );
}
